import React, { useState } from 'react';
import PageTitle from 'components/common/PageTitle';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { RoutePaths } from 'Constants/Common';
import { MissedPaymentResponse } from 'api/types/payment';
import { PaymentApi } from 'api/PaymentApi';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import WorldPayForm from './worldPay/WorldPayForm';
import { useAppSelector } from 'state/hooks';
import { selectMissedPaymentByPolicy } from 'state/selectors/vehicles';
import { selectCurrency } from 'state/selectors/site';
import { buildMissedPaymentSuccessPath } from 'helpers/routing';

export const PaymentTypes = {
    MissedPayment: 'missed-payment'
} as const;

// Consider in case of different types of payments
// splitting this component into several
// or extracting the logic into hook or something else

const PaymentPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { type: paymentType, id } = useParams();
    const { getAccessTokenSilently } = useAuth0();

    const [paymentUrl, setPaymentUrl] = useState('');

    const currency = useAppSelector(selectCurrency);
    const missedPayment = useAppSelector((s) =>
        selectMissedPaymentByPolicy(s, id)
    );

    const amount =
        paymentType === PaymentTypes.MissedPayment ? missedPayment?.amount : 0;

    const amountToPay = `${currency}${amount}`;

    if (!amount || paymentType !== PaymentTypes.MissedPayment)
        return <Navigate to={RoutePaths.Home} replace />;

    const missedPaymentRequest = async () => {
        const callback = (data: MissedPaymentResponse) => {
            if (data.redirectUrl) {
                setPaymentUrl(data.redirectUrl);
            } else {
                toast.error('Payment Can not be done, try again');
            }
        };

        const failureCallback = () => {
            toast.error(t('Payment Error'));
        };

        const api = new PaymentApi();
        const token = await getAccessTokenSilently();

        api.sendOrder(token, id, callback, failureCallback);
    };

    const onPaymentSuccess = async () => {
        await new Promise((r) => setTimeout(r, 1500)); // wait to see success from worldpay
        if (paymentType === PaymentTypes.MissedPayment)
            navigate(buildMissedPaymentSuccessPath(id));
    };

    const onPaymentPageReady = async () => {
        if (paymentType === PaymentTypes.MissedPayment) {
            await missedPaymentRequest();
            return;
        }

        if (paymentType !== PaymentTypes.MissedPayment) {
            console.debug('This type of payment is not supported');
        }
    };

    return (
        <div className='page-content'>
            <PageTitle title={t('payment.single_payment.page_title')} />
            <div className='page-content-body p-3 flex-column d-flex gap-3'>
                <div className='card'>
                    <div className='card-body'>
                        <span className='fw-bold'>
                            {t('missed_payment.amount_due')}
                        </span>
                        <span className='ms-2'>{amountToPay}</span>
                    </div>
                </div>
                <WorldPayForm
                    paymentUrl={paymentUrl}
                    onPageReady={onPaymentPageReady}
                    onPaymentSuccess={onPaymentSuccess}
                />
            </div>
        </div>
    );
};

export default PaymentPage;
